/**
 * CompanyBackupView.tsx
 * ─────────────────────
 * Kaam 3 — Auto Local Backup
 *
 * Tabs:
 *  1. Manual Backup  — download now
 *  2. Auto Backup    — interval + folder settings + recent backup list
 */

import { useState, useEffect } from 'react';
import { db } from '../db/db';
import { useStore } from '../store/useStore';
import {
  performBackup,
  getAutoBackupSettings,
  saveAutoBackupSettings,
  restartAutoBackup,
  stopAutoBackup,
  getRecentBackups,
  BackupInterval,
  AutoBackupSettings,
  BackupMeta,
} from '../services/autoBackupService';

const isTauri = (): boolean =>
  typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window;

const INTERVAL_LABELS: { value: BackupInterval; label: string }[] = [
  { value: 'off',   label: 'Off (Disabled)' },
  { value: '30min', label: 'Every 30 Minutes' },
  { value: '1hr',   label: 'Every 1 Hour' },
  { value: '3hr',   label: 'Every 3 Hours' },
  { value: 'daily', label: 'Once Daily' },
];

const formatBytes = (b: number) =>
  b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(2)} MB`;

const formatDate = (iso: string) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
};

export const CompanyBackupView = ({ handleItemSelect, companyId }: any) => {
  const activeCompany = useStore((state) => state.activeCompany);
  const licenseInfo   = useStore((state) => state.licenseInfo);
  const setShowLicensePanel = useStore((state) => state.setShowLicensePanel);
  const resolvedId = companyId ?? activeCompany?.id;

  // ── Trial / Demo guard — show upgrade wall, never render backup UI ─────────
  if (licenseInfo?.type === 'demo' || !licenseInfo) {
    return (
      <div className="flex flex-col w-full h-full bg-[#f8faf7] z-50 absolute inset-0 font-sans text-[13px] text-black border-2 border-[#1e8585]">
        <div className="bg-[#1e8585] text-white flex justify-between px-2 py-[2px] font-bold shadow-sm">
          <span>Backup Company</span>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
          <div className="bg-white border border-[#1e8585] shadow-lg p-8 max-w-md w-full">
            <div className="text-4xl mb-4">🔒</div>
            <h2 className="text-[15px] font-bold mb-2 text-[#1e8585]">Backup &amp; Restore — Full Version Only</h2>
            <p className="text-gray-600 mb-6 text-[12px]">
              Trial mode mein Backup aur Restore feature available nahi hai.<br />
              Apna data protect karne ke liye full license activate karein.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => { setShowLicensePanel(true); handleItemSelect({ name: 'Quit' }); }}
                className="bg-[#1e8585] text-white px-6 py-2 font-bold hover:bg-[#1b7373] shadow-md"
              >
                License Activate Karein
              </button>
              <button
                onClick={() => handleItemSelect({ name: 'Quit' })}
                className="bg-gray-200 px-6 py-2 font-bold hover:bg-gray-300 shadow-md"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
        <div className="bg-[#1e8585] text-white text-[11px] px-2 py-1 flex justify-between">
          <span>Esc: Quit</span>
          <span onClick={() => handleItemSelect({ name: 'Quit' })} className="cursor-pointer hover:underline">Close</span>
        </div>
      </div>
    );
  }

  const [tab, setTab] = useState<'manual' | 'auto'>('manual');
  const [manualStatus, setManualStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [manualMsg, setManualMsg]       = useState('');

  // Auto-backup settings state
  const [settings, setSettings]         = useState<AutoBackupSettings>(getAutoBackupSettings);
  const [saveStatus, setSaveStatus]     = useState<'idle' | 'saved' | 'error'>('idle');
  const [recentBackups, setRecentBackups] = useState<BackupMeta[]>([]);

  useEffect(() => {
    if (resolvedId) setRecentBackups(getRecentBackups(resolvedId));
  }, [resolvedId, tab]);

  // ── Manual Backup ─────────────────────────────────────────────────────────
  const handleManualBackup = async () => {
    if (!resolvedId) { setManualMsg('No company selected.'); setManualStatus('error'); return; }
    setManualStatus('loading');
    setManualMsg('');
    const result = await performBackup(resolvedId, undefined, false);
    setManualStatus(result.success ? 'done' : 'error');
    setManualMsg(result.message);
    if (result.success) setRecentBackups(getRecentBackups(resolvedId));
  };

  // ── Auto Backup Settings ──────────────────────────────────────────────────
  const handleFolderPick = async () => {
    if (!isTauri()) return;
    try {
      const { open } = await import('@tauri-apps/plugin-dialog');
      const selected = await open({ directory: true, multiple: false, title: 'Choose Backup Folder' });
      if (typeof selected === 'string' && selected) {
        setSettings(prev => ({ ...prev, folder: selected }));
      }
    } catch (err) {
      console.error('Folder picker error:', err);
    }
  };

  const handleSaveAutoSettings = () => {
    try {
      saveAutoBackupSettings(settings);
      if (resolvedId) {
        if (settings.interval === 'off') {
          stopAutoBackup();
        } else {
          restartAutoBackup(resolvedId);
        }
      }
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2500);
    } catch {
      setSaveStatus('error');
    }
  };

  // ── UI ────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col w-full h-full bg-[#f8faf7] z-50 absolute inset-0 font-sans text-[13px] text-black border-2 border-[#1e8585]">
      {/* Header */}
      <div className="bg-[#1e8585] text-white flex justify-between px-2 py-[2px] font-bold shadow-sm">
        <span>Backup Company</span>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-300 bg-[#e8f4f4]">
        {(['manual', 'auto'] as const).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-6 py-1 font-bold text-[13px] border-r border-gray-300 transition-colors ${
              tab === t ? 'bg-white text-[#1e8585] border-b-2 border-b-[#1e8585]' : 'text-gray-600 hover:bg-white'
            }`}
          >
            {t === 'manual' ? 'Manual Backup' : 'Auto Backup Settings'}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-4">

        {/* ── Manual Backup Tab ─────────────────────────────────────────── */}
        {tab === 'manual' && (
          <div className="flex flex-col items-center justify-center h-full">
            <div className="bg-white border border-gray-300 shadow-lg p-8 max-w-md w-full text-center">
              <h2 className="text-[15px] font-bold mb-2 text-[#1e8585]">Export Company Data</h2>
              <p className="text-gray-600 mb-6 text-[12px]">
                Download a full backup of this company's data as a JSON file.
                {isTauri() && getAutoBackupSettings().folder
                  ? ` File will also be saved to: ${getAutoBackupSettings().folder}`
                  : ''}
              </p>

              {manualStatus === 'loading' && (
                <p className="text-[#1e8585] font-bold mb-4 animate-pulse">⟳ Creating backup...</p>
              )}
              {manualStatus === 'done' && (
                <p className="text-green-700 font-bold mb-4">✔ {manualMsg}</p>
              )}
              {manualStatus === 'error' && (
                <p className="text-red-600 font-bold mb-4">✘ {manualMsg}</p>
              )}

              <div className="flex gap-4 justify-center">
                <button
                  onClick={handleManualBackup}
                  disabled={manualStatus === 'loading'}
                  className="bg-[#1e8585] text-white px-8 py-2 font-bold hover:bg-[#1b7373] shadow-md disabled:opacity-50"
                >
                  Download Backup
                </button>
                <button
                  onClick={() => handleItemSelect({ name: 'Quit' })}
                  className="bg-gray-200 px-8 py-2 font-bold hover:bg-gray-300 shadow-md"
                >
                  Cancel
                </button>
              </div>

              {/* Recent Backups — quick list */}
              {recentBackups.length > 0 && (
                <div className="mt-6 text-left">
                  <p className="font-bold text-[12px] text-gray-700 mb-1">Recent Backups:</p>
                  <div className="border border-gray-200 divide-y divide-gray-100 max-h-[160px] overflow-y-auto">
                    {recentBackups.map((b, i) => (
                      <div key={i} className="flex justify-between px-2 py-1 text-[11px] text-gray-600">
                        <span className="truncate flex-1 mr-2">{b.filename}</span>
                        <span className="text-gray-400 whitespace-nowrap">{formatBytes(b.sizeBytes)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Auto Backup Tab ───────────────────────────────────────────── */}
        {tab === 'auto' && (
          <div className="max-w-lg">
            <div className="bg-white border border-gray-300 shadow p-6 mb-4">
              <h2 className="text-[14px] font-bold text-[#1e8585] mb-4">Auto Backup Configuration</h2>

              {/* Interval */}
              <div className="mb-4">
                <label className="block font-bold mb-1">Backup Interval</label>
                <select
                  value={settings.interval}
                  onChange={e => setSettings(prev => ({ ...prev, interval: e.target.value as BackupInterval }))}
                  className="w-full border border-gray-300 px-2 py-1 bg-white focus:outline-none focus:border-[#1e8585]"
                >
                  {INTERVAL_LABELS.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              {/* Folder — Tauri only */}
              {isTauri() ? (
                <div className="mb-4">
                  <label className="block font-bold mb-1">Backup Folder</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={settings.folder || 'No folder selected'}
                      className="flex-1 border border-gray-300 px-2 py-1 bg-gray-50 text-[12px] truncate"
                    />
                    <button
                      onClick={handleFolderPick}
                      className="bg-[#1e8585] text-white px-3 py-1 font-bold hover:bg-[#1b7373] whitespace-nowrap"
                    >
                      Browse…
                    </button>
                  </div>
                  {!settings.folder && settings.interval !== 'off' && (
                    <p className="text-amber-600 text-[11px] mt-1">
                      ⚠ Select a folder for auto-backup to work.
                    </p>
                  )}
                </div>
              ) : (
                <div className="mb-4 bg-amber-50 border border-amber-200 p-2 text-[11px] text-amber-700">
                  Auto-backup to local folder is only available in the Tauri desktop app.
                  In browser mode, manual backup will trigger a download.
                </div>
              )}

              {/* Max Backups */}
              <div className="mb-4">
                <label className="block font-bold mb-1">Keep Last N Backups</label>
                <select
                  value={settings.maxBackups}
                  onChange={e => setSettings(prev => ({ ...prev, maxBackups: Number(e.target.value) }))}
                  className="w-full border border-gray-300 px-2 py-1 bg-white focus:outline-none focus:border-[#1e8585]"
                >
                  {[3, 5, 7, 10].map(n => (
                    <option key={n} value={n}>Last {n} backups</option>
                  ))}
                </select>
              </div>

              {/* Last backup info */}
              {settings.lastBackupAt && (
                <p className="text-[11px] text-gray-500 mb-3">
                  Last backup: {formatDate(settings.lastBackupAt)}
                </p>
              )}

              {/* Save button */}
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSaveAutoSettings}
                  className="bg-[#1e8585] text-white px-6 py-1 font-bold hover:bg-[#1b7373] shadow"
                >
                  Save Settings
                </button>
                {saveStatus === 'saved' && <span className="text-green-700 font-bold text-[12px]">✔ Saved!</span>}
                {saveStatus === 'error' && <span className="text-red-600 font-bold text-[12px]">✘ Error saving</span>}
              </div>
            </div>

            {/* Backup History */}
            <div className="bg-white border border-gray-300 shadow p-4">
              <h3 className="font-bold text-[13px] text-[#1e8585] mb-2">
                Backup History{resolvedId ? ` — This Company` : ''}
              </h3>
              {recentBackups.length === 0 ? (
                <p className="text-gray-500 text-[12px]">No backups recorded yet.</p>
              ) : (
                <div className="divide-y divide-gray-100 max-h-[220px] overflow-y-auto">
                  <div className="flex font-bold text-[11px] text-gray-500 px-1 py-1 bg-gray-50">
                    <span className="flex-[3]">Filename</span>
                    <span className="flex-1 text-right">Size</span>
                    <span className="flex-[2] text-right">Date</span>
                  </div>
                  {recentBackups.map((b, i) => (
                    <div key={i} className="flex px-1 py-1 text-[11px] text-gray-700 hover:bg-gray-50">
                      <span className="flex-[3] truncate mr-1">{b.filename}</span>
                      <span className="flex-1 text-right text-gray-400">{formatBytes(b.sizeBytes)}</span>
                      <span className="flex-[2] text-right text-gray-400">{formatDate(b.createdAt)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="bg-[#1e8585] text-white text-[11px] px-2 py-1 flex justify-between">
        <span>Tab: Switch View | Esc: Quit</span>
        <span onClick={() => handleItemSelect({ name: 'Quit' })} className="cursor-pointer hover:underline">
          Close
        </span>
      </div>
    </div>
  );
};
